import React from 'react';
import { Check, AlertCircle, Edit2, ShieldCheck, Sparkles, Clock, Award } from 'lucide-react';
import { toast } from 'sonner';

// Compute per-section completion + required status
const buildChecklist = (formData) => {
  const p = formData.personal || {};
  const prof = formData.professional || {};
  const edu = formData.education || [];
  const prac = formData.practice || [];
  const avail = formData.availability || {};
  const exp = formData.experience || [];
  const docs = formData.documents || {};
  const bank = docs.bank_details || {};

  const availDaysEnabled = avail?.weekly?.days
    ? Object.values(avail.weekly.days).filter((d) => d?.enabled).length
    : 0;

  const docCount = ['registration_cert', 'degree_cert', 'id_proof', 'avatar']
    .filter((k) => docs[k]).length;

  const bankReady =
    bank.clinic_managed_payout ||
    (bank.payout_method === 'upi' && bank.upi_id) ||
    (bank.payout_method === 'bank_transfer' && bank.account_number && bank.ifsc && bank.account_holder);

  return [
    {
      id: 1,
      title: 'Personal Information',
      required: true,
      done: !!(p.first_name && p.gender),
      detail: `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Not filled',
    },
    {
      id: 2,
      title: 'Professional Details',
      required: true,
      done: !!(prof.primary_specialization && prof.medical_registration_number),
      detail: prof.primary_specialization ? `${prof.primary_specialization} · Reg # ${prof.medical_registration_number || '—'}` : 'Not filled',
    },
    {
      id: 3,
      title: 'Education',
      required: false,
      done: edu.length > 0,
      detail: edu.length ? `${edu.length} qualification${edu.length > 1 ? 's' : ''} added` : 'None added (optional)',
    },
    {
      id: 4,
      title: 'Practice Locations',
      required: false,
      done: prac.length > 0,
      detail: prac.length ? `${prac.length} location${prac.length > 1 ? 's' : ''} added` : 'None added',
    },
    {
      id: 5,
      title: 'Availability',
      required: false,
      done: availDaysEnabled > 0,
      detail: availDaysEnabled ? `${availDaysEnabled} day${availDaysEnabled > 1 ? 's' : ''} enabled` : 'No days enabled',
    },
    {
      id: 6,
      title: 'Work Experience',
      required: false,
      done: exp.length > 0,
      detail: exp.length ? `${exp.length} role${exp.length > 1 ? 's' : ''} added` : 'None added (optional)',
    },
    {
      id: 7,
      title: 'Documents & Bank',
      required: true,
      done: docCount === 4 && bankReady,
      detail: `${docCount}/4 documents · Bank ${bankReady ? 'ready' : 'incomplete'}`,
    },
  ];
};

export default function Step8Review({ formData = {}, onJumpTo, onSubmit, saving }) {
  const items = buildChecklist(formData);
  const doneCount = items.filter((i) => i.done).length;
  const percent = Math.round((doneCount / items.length) * 100);
  const missingRequired = items.filter((i) => i.required && !i.done);
  const canSubmit = missingRequired.length === 0;

  return (
    <form
      id="step-form-8"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) {
          onSubmit();
        } else {
          toast.error(`Please complete: ${missingRequired.map((m) => m.title).join(', ')}`);
        }
      }}
      className="p-6 lg:p-8 space-y-6"
    >
      {/* Hero completion card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#3B1C73] to-[#5B2FAA] p-6 text-white">
        <div className="absolute inset-0 opacity-10">
          <Sparkles className="w-40 h-40 absolute -right-6 -top-6" />
        </div>
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-white/70 uppercase tracking-wider">Almost there!</p>
            <h3 className="text-2xl font-bold mt-1">Review Your Profile</h3>
            <p className="text-white/80 mt-2 text-sm max-w-md">
              Take a final look. Once submitted, our team will verify your credentials within 24 hours.
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-4xl font-bold">{percent}%</div>
            <div className="text-xs text-white/70">Completion</div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white transition-all duration-500" style={{ width: `${percent}%` }} />
        </div>
      </div>

      {/* Missing required warning */}
      {!canSubmit && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3" data-testid="review-missing-warning">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-900 text-sm">Please complete the following required sections:</p>
            <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
              {missingRequired.map((m) => (
                <li key={m.id}>
                  <button type="button" onClick={() => onJumpTo(m.id)} className="underline hover:text-red-900">
                    {m.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Checklist */}
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className={`p-4 rounded-xl border transition-all ${
              item.done
                ? 'border-emerald-200 bg-emerald-50/40'
                : item.required
                ? 'border-red-200 bg-red-50/40'
                : 'border-slate-200 bg-white'
            } flex items-center gap-4`}
            data-testid={`review-item-${item.id}`}
          >
            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                item.done
                  ? 'bg-emerald-500 text-white'
                  : item.required
                  ? 'bg-red-100 text-red-600'
                  : 'bg-slate-100 text-slate-400'
              }`}
            >
              {item.done ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-slate-900 text-sm">{item.title}</p>
                {item.required && !item.done && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Required</span>
                )}
                {!item.required && !item.done && (
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">Optional</span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{item.detail}</p>
            </div>
            <button
              type="button"
              onClick={() => onJumpTo(item.id)}
              className="text-slate-500 hover:text-[#3B1C73] hover:bg-white p-2 rounded-lg text-xs font-medium flex items-center gap-1"
              data-testid={`review-edit-${item.id}`}
            >
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          </div>
        ))}
      </div>

      {/* What happens next */}
      <div className="grid sm:grid-cols-3 gap-3 pt-2">
        <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
          <ShieldCheck className="w-6 h-6 text-blue-600 mb-2" />
          <p className="font-semibold text-slate-900 text-sm">1. Verification</p>
          <p className="text-xs text-slate-600 mt-1">Our team reviews your documents within 24 hours.</p>
        </div>
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
          <Clock className="w-6 h-6 text-amber-600 mb-2" />
          <p className="font-semibold text-slate-900 text-sm">2. Approval</p>
          <p className="text-xs text-slate-600 mt-1">You&apos;ll receive email/SMS once approved.</p>
        </div>
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
          <Award className="w-6 h-6 text-emerald-600 mb-2" />
          <p className="font-semibold text-slate-900 text-sm">3. Go Live</p>
          <p className="text-xs text-slate-600 mt-1">Verified badge activates, patients can book you.</p>
        </div>
      </div>

      {/* Terms */}
      <p className="text-xs text-slate-500 text-center pt-2">
        By submitting, you confirm all information is accurate and you agree to Physign&apos;s{' '}
        <a href="/terms" target="_blank" rel="noreferrer" className="text-[#3B1C73] underline">Terms of Service</a>.
      </p>

      {/* Hidden submit — actual button is in wizard footer */}
      <button type="submit" className="hidden" disabled={!canSubmit || saving} data-testid="review-submit-hidden" />
    </form>
  );
}
